import { Button, TextInput, useButtonConfirmDialog, useForm, useToast } from "@rocketmakers/armstrong";
import * as React from "react";
import { BeeStatus, RouterId } from "../../../../shared/api-clients/_api";
import { Message, MessageTypes } from "../../../../shared/components/message";
import { useRoutePaths } from "../../../../shared/context/paths";
import { InputValidator, ValidatorAndMessage } from "../../../../shared/hooks/ValidationTypes";
import { useValidatedForm } from "../../../../shared/hooks/formValidationHook";
import { useToastOnError } from "../../../../shared/hooks/useApiError";
import { useBee } from "../../../../shared/hooks/useBee";
import { BackButton } from "../../components/buttons/back-button";
import { Spinner } from "../../components/spinner/spinner";
import { useBeeConfig } from "../../hooks/beeConfig";
import { routePaths } from "../../routes";
import "./device.scss";

interface IBeeForm {
	macAddress: string;
}

export const macAddressValidator: ValidatorAndMessage = {
	validate: (macAddress) => macAddress?.match(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/),
	message: "Must be a valid mac address",
};

const validators: InputValidator<IBeeForm>[] = [
	{
		key: "macAddress",
		validators: [macAddressValidator],
	},
];

export const DeviceView: React.FunctionComponent = () => {
	const { push } = useRoutePaths();
	const { findBee, error, beeInfo, loading } = useBee();
	const { pushDevicesToConfig } = useBeeConfig();
	const { dispatch } = useToast();

	const initialState: IBeeForm = React.useMemo(
		() => ({
			macAddress: beeInfo?.bee?.routerId,
		}),
		[beeInfo],
	);
	const { DataForm, bind, dataBinder } = useForm(initialState);
	const { setTouched, formIsValid, validationErrors } = useValidatedForm<IBeeForm>(validators, dataBinder);

	const isBeeOnline = beeInfo?.status === BeeStatus.Online;

	useToastOnError(error);

	const handleFindBee = async () => {
		const { macAddress } = dataBinder.toJson();
		findBee({ macAddress: macAddress.toLowerCase() as RouterId });
	};

	const handleContinue = () => {
		const nextRoute = routePaths.device.sticker;
		push(nextRoute, { deviceId: beeInfo.bee.deviceId });
	};

	const deletable = useButtonConfirmDialog({
		content:
			"Are you sure you want to delete the configuration? If the IES has already been commissioned, it will need to be commisioned again.",
		confirmText: "Yes, Delete",
		cancelText: "Cancel",
	});

	const handleResetBeeConfiguration = async () => {
		await pushDevicesToConfig([]);
		dispatch({ title: "Configuration has been deleted", type: "success" });
	};

	const statusMessageMap = {
		[BeeStatus.Online]: {
			type: MessageTypes.Success,
			title: "BEE Online",
			message: (
				<p>
					We've found a device with this MAC address and appears to be <strong>online.</strong>
				</p>
			),
		},
		[BeeStatus.Offline]: {
			type: MessageTypes.Warning,
			title: "BEE Offline",
			message: (
				<>
					<p>
						We've found a device with this MAC address, but appears to be <strong>offline.</strong>
					</p>
					<p>Please check that:</p>{" "}
					<ol>
						<li>the device is powered up and</li> <li>the signal strength LED is ON or Flashing.</li>
					</ol>
				</>
			),
		},
		[BeeStatus.NotFound]: {
			type: MessageTypes.Error,
			title: "BEE not found",
			message: (
				<>
					<p>We can't find a device with this MAC address.</p>
					<p>Please re-enter the MAC address and search again.</p>
				</>
			),
		},
	};

	const statusMessage = statusMessageMap[beeInfo?.status];

	return (
		<DataForm validationResults={validationErrors} validationMode="both">
			{loading && <Spinner />}
			<BackButton />
			<h1>
				Mac <span className="color-primary">Address</span>
			</h1>

			<TextInput
				{...bind.text("macAddress")}
				label="Enter the Mac address of the BEE"
				onBlur={setTouched}
				data-qa="mac"
			/>

			<Button
				className="btn-outline search-button btn-full"
				rightIcon={<img src={require("../../../assets/images/installer/magnifier.svg")} alt="" width={24} />}
				disabled={formIsValid === false}
				onClick={handleFindBee}
				data-qa="search-device"
			>
				Search
			</Button>

			{beeInfo?.status && (
				<Message type={statusMessage.type} title={statusMessage.title}>
					{statusMessage.message}
				</Message>
			)}

			<Button
				className="btn-full secondary-button"
				disabled={!beeInfo}
				onClick={async () => {
					if (await deletable()) {
						await handleResetBeeConfiguration();
					}
				}}
				data-qa="reset-bee-configuration"
			>
				Delete existing configuration
			</Button>

			<div className="row">
				<Button
					data-qa="continue-button"
					id="save-future-update"
					className="continue-button btn-full btn-large"
					disabled={!isBeeOnline}
					onClick={handleContinue}
				>
					Continue
				</Button>
			</div>
		</DataForm>
	);
};
